/**
 * Budget Proposals Page
 * AIDLC Administrative Backend
 *
 * CEO's proposal approval interface. Shows a card grid of all proposals
 * with filtering by status. Clicking a card opens the detail view with
 * budget breakdown, stories table, and action buttons (submit, approve,
 * decline). Consumes the backend /api/proposals endpoints.
 *
 * Personas: Executive, Ops
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import type {
  Proposal,
  ProposalStatus,
  ProposalStory,
  ProposalBudget,
} from '../../services/orchestrationService';
import {
  getProposals,
  submitProposal,
  approveProposal,
  declineProposal,
} from '../../services/orchestrationService';

import { fmtNum, fmtTokens, fmtDate } from '../../utils/formatters';

/** Status badge variant mapping */
const statusConfig: Record<ProposalStatus, { label: string; variant: 'neutral' | 'warning' | 'success' | 'danger' }> = {
  draft: { label: 'Draft', variant: 'neutral' },
  pending_review: { label: 'Pending Review', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  declined: { label: 'Declined', variant: 'danger' },
};

/** MoSCoW priority color classes */
const priorityConfig: Record<string, { label: string; bg: string; text: string }> = {
  must: { label: 'Must', bg: 'bg-red-50', text: 'text-red-700' },
  should: { label: 'Should', bg: 'bg-orange-50', text: 'text-orange-700' },
  could: { label: 'Could', bg: 'bg-blue-50', text: 'text-blue-700' },
  wont: { label: "Won't", bg: 'bg-gray-100', text: 'text-gray-500' },
};

/** Size badge color */
const sizeConfig: Record<string, { bg: string; text: string }> = {
  S: { bg: 'bg-green-50', text: 'text-green-700' },
  M: { bg: 'bg-blue-50', text: 'text-blue-700' },
  L: { bg: 'bg-orange-50', text: 'text-orange-700' },
  XL: { bg: 'bg-red-50', text: 'text-red-700' },
};

type FilterOption = 'all' | ProposalStatus;

// ── Main Component ───────────────────────────────────────────────────

export function BudgetProposalsPage() {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadProposals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getProposals();
      setProposals(data);
    } catch (err) {
      console.error('[BudgetProposals] Failed to load:', err);
      setError('Failed to load proposals. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  const filteredProposals = filter === 'all'
    ? proposals
    : proposals.filter((p) => p.status === filter);

  const counts = {
    all: proposals.length,
    draft: proposals.filter((p) => p.status === 'draft').length,
    pending_review: proposals.filter((p) => p.status === 'pending_review').length,
    approved: proposals.filter((p) => p.status === 'approved').length,
    declined: proposals.filter((p) => p.status === 'declined').length,
  };

  const selectedProposal = selectedId
    ? proposals.find((p) => p.id === selectedId) || null
    : null;

  const handleAction = async () => {
    await loadProposals();
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading budget proposals..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Proposals</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadProposals}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Detail View
  if (selectedProposal) {
    return (
      <ProposalDetailView
        proposal={selectedProposal}
        onBack={() => setSelectedId(null)}
        onAction={handleAction}
      />
    );
  }

  // List View
  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Budget Proposals</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review, approve, or decline token budget proposals for planned work packages.
          </p>
        </div>
        <button
          onClick={loadProposals}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-1 mb-6">
        {([
          { key: 'all' as FilterOption, label: 'All' },
          { key: 'draft' as FilterOption, label: 'Draft' },
          { key: 'pending_review' as FilterOption, label: 'Pending Review' },
          { key: 'approved' as FilterOption, label: 'Approved' },
          { key: 'declined' as FilterOption, label: 'Declined' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === key
                ? 'bg-ldaja-blue text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {/* Proposal Cards Grid */}
      {filteredProposals.length === 0 ? (
        <EmptyState
          title="No proposals found"
          description={
            filter === 'all'
              ? 'No budget proposals have been created yet.'
              : `No proposals with status "${statusConfig[filter as ProposalStatus]?.label || filter}".`
          }
          action={
            filter !== 'all' ? (
              <button
                onClick={() => setFilter('all')}
                className="text-sm text-ldaja-blue hover:underline"
              >
                Clear filter
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredProposals.map((proposal) => (
            <ProposalCard
              key={proposal.id}
              proposal={proposal}
              onClick={() => setSelectedId(proposal.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Proposal Card ────────────────────────────────────────────────────

function ProposalCard({ proposal, onClick }: { proposal: Proposal; onClick: () => void }) {
  const status = statusConfig[proposal.status] || statusConfig.draft;
  const totalBudget = proposal.budget?.totalBudget || 0;

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-ldaja-blue/40 hover:shadow-md transition-all cursor-pointer group"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ldaja-black truncate group-hover:text-ldaja-blue transition-colors">
            {proposal.title}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {proposal.projectId}
          </p>
        </div>
        <Badge variant={status.variant} dot>
          {status.label}
        </Badge>
      </div>

      {/* Description */}
      {proposal.description && (
        <p className="text-xs text-gray-500 mb-3 line-clamp-2">
          {proposal.description}
        </p>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-4 text-xs">
        <div>
          <span className="text-gray-400">Budget: </span>
          <span className="font-semibold text-gray-700">{fmtTokens(totalBudget)} tokens</span>
        </div>
        <div>
          <span className="text-gray-400">Stories: </span>
          <span className="font-semibold text-gray-700">{proposal.stories?.length || 0}</span>
        </div>
      </div>

      {/* Date */}
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
        Created {fmtDate(proposal.createdAt)}
        {proposal.reviewedAt && (
          <span> &middot; Reviewed {fmtDate(proposal.reviewedAt)}</span>
        )}
      </div>
    </div>
  );
}

// ── Proposal Detail View ─────────────────────────────────────────────

function ProposalDetailView({
  proposal,
  onBack,
  onAction,
}: {
  proposal: Proposal;
  onBack: () => void;
  onAction: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = statusConfig[proposal.status] || statusConfig.draft;
  const budget = proposal.budget || {
    totalBudget: 0,
    orchestrationOverhead: 0,
    dodOverhead: 0,
    reworkBuffer: 0,
    netAvailable: 0,
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setActionError(null);
    try {
      await submitProposal(proposal.id);
      onAction();
    } catch (err) {
      setActionError('Failed to submit proposal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    setActionError(null);
    try {
      await approveProposal(proposal.id, 'CEO');
      onAction();
    } catch (err) {
      setActionError('Failed to approve proposal. Please try again.');
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return;
    setIsDeclining(true);
    setActionError(null);
    try {
      await declineProposal(proposal.id, 'CEO', declineReason.trim());
      onAction();
    } catch (err) {
      setActionError('Failed to decline proposal. Please try again.');
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-ldaja-blue transition-colors mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path d="M15 19l-7-7 7-7" />
        </svg>
        Back to Proposals
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-ldaja-black">{proposal.title}</h1>
            <Badge variant={status.variant} dot>
              {status.label}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Project: {proposal.projectId} &middot; Created {fmtDate(proposal.createdAt)}
          </p>
          {proposal.description && (
            <p className="text-sm text-gray-600 mt-2 max-w-2xl">{proposal.description}</p>
          )}
        </div>
      </div>

      {/* Action Error */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{actionError}</p>
          <button onClick={() => setActionError(null)} className="text-red-600 text-xs underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Budget Breakdown */}
      <Card title="Budget Breakdown" subtitle="Token allocation across overhead categories" className="mb-6">
        <BudgetBreakdown budget={budget} />
      </Card>

      {/* Stories Table */}
      <Card
        title="Included Stories"
        subtitle={`${proposal.stories?.length || 0} stories in this proposal`}
        className="mb-6"
      >
        <StoriesTable stories={proposal.stories || []} />
      </Card>

      {/* Budget Summary */}
      <Card title="Budget Summary" className="mb-6">
        <BudgetSummaryRow budget={budget} storyCount={proposal.stories?.length || 0} />
      </Card>

      {/* Review Info (approved/declined) */}
      {proposal.status === 'approved' && proposal.reviewedBy && (
        <Card title="Approval Info" className="mb-6">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-400">Approved by: </span>
              <span className="font-medium text-green-700">{proposal.reviewedBy}</span>
            </div>
            {proposal.reviewedAt && (
              <div>
                <span className="text-gray-400">Date: </span>
                <span className="font-medium text-gray-700">{fmtDate(proposal.reviewedAt)}</span>
              </div>
            )}
            {proposal.milestoneId && (
              <div>
                <span className="text-gray-400">Milestone: </span>
                <span className="font-medium text-ldaja-blue">{proposal.milestoneId}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {proposal.status === 'declined' && (
        <Card title="Decline Info" className="mb-6">
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-400">Declined by: </span>
              <span className="font-medium text-red-700">{proposal.reviewedBy || 'Unknown'}</span>
            </div>
            {proposal.declineReason && (
              <div>
                <span className="text-gray-400">Reason: </span>
                <span className="text-gray-700">{proposal.declineReason}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-2">
        {proposal.status === 'draft' && (
          <>
            <Button
              variant="primary"
              size="md"
              isLoading={isSubmitting}
              onClick={handleSubmit}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Submit for Review
            </Button>
          </>
        )}

        {proposal.status === 'pending_review' && (
          <>
            <Button
              variant="primary"
              size="md"
              isLoading={isApproving}
              onClick={handleApprove}
              className="bg-green-600 hover:bg-green-700 focus:ring-green-500/50"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M5 13l4 4L19 7" />
              </svg>
              Approve
            </Button>

            {!showDeclineForm ? (
              <Button
                variant="danger"
                size="md"
                onClick={() => setShowDeclineForm(true)}
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
                Decline
              </Button>
            ) : (
              <div className="flex items-end gap-2 flex-1">
                <div className="flex-1 max-w-md">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Decline Reason
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
                    placeholder="Provide a reason for declining..."
                  />
                </div>
                <Button
                  variant="danger"
                  size="md"
                  isLoading={isDeclining}
                  disabled={!declineReason.trim()}
                  onClick={handleDecline}
                >
                  Confirm Decline
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setShowDeclineForm(false);
                    setDeclineReason('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}

        {proposal.status === 'declined' && (
          <Button variant="secondary" size="md" onClick={onBack}>
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Return to List
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Budget Breakdown Visualization ───────────────────────────────────

function BudgetBreakdown({ budget }: { budget: ProposalBudget }) {
  const total = budget.totalBudget || 1; // prevent div by zero

  const segments = [
    { label: 'Dev Work (Net Available)', value: budget.netAvailable, color: 'bg-ldaja-blue' },
    { label: 'Orchestration Overhead', value: budget.orchestrationOverhead, color: 'bg-purple-500' },
    { label: 'DoD Overhead', value: budget.dodOverhead, color: 'bg-amber-500' },
    { label: 'Rework Buffer', value: budget.reworkBuffer, color: 'bg-gray-400' },
  ];

  return (
    <div>
      {/* Stacked Bar */}
      <div className="h-8 rounded-lg overflow-hidden flex mb-4">
        {segments.map((seg) => {
          const pct = (seg.value / total) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={seg.label}
              className={`${seg.color} flex items-center justify-center transition-all`}
              style={{ width: `${pct}%` }}
              title={`${seg.label}: ${fmtTokens(seg.value)} (${pct.toFixed(1)}%)`}
            >
              {pct > 8 && (
                <span className="text-white text-[10px] font-medium truncate px-1">
                  {pct.toFixed(0)}%
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {segments.map((seg) => {
          const pct = (seg.value / total) * 100;
          return (
            <div key={seg.label} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-sm shrink-0 ${seg.color}`} />
              <div>
                <p className="text-xs font-medium text-gray-700">{seg.label}</p>
                <p className="text-xs text-gray-400">
                  {fmtTokens(seg.value)} ({pct.toFixed(1)}%)
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Stories Table ─────────────────────────────────────────────────────

function StoriesTable({ stories }: { stories: ProposalStory[] }) {
  if (stories.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-6 text-center">No stories included in this proposal.</p>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
            <th className="px-4 py-2 font-medium">Title</th>
            <th className="px-4 py-2 font-medium">Size</th>
            <th className="px-4 py-2 font-medium text-right">Token Estimate</th>
            <th className="px-4 py-2 font-medium">Priority</th>
            <th className="px-4 py-2 font-medium">Required Agents</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {stories.map((story, idx) => {
            const size = sizeConfig[story.sizeCategory] || sizeConfig.M;
            const priority = priorityConfig[story.priority] || priorityConfig.could;
            return (
              <tr key={story.backlogItemId || idx} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-medium text-gray-900">{story.title}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${size.bg} ${size.text}`}>
                    {story.sizeCategory}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-gray-700 tabular-nums font-medium">
                  {fmtNum(story.tokenEstimate)}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${priority.bg} ${priority.text}`}>
                    {priority.label}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {story.requiredAgents?.map((agent) => (
                      <span
                        key={agent}
                        className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded"
                      >
                        {agent}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Budget Summary Row ───────────────────────────────────────────────

function BudgetSummaryRow({ budget, storyCount }: { budget: ProposalBudget; storyCount: number }) {
  const perStoryAvg = storyCount > 0 ? Math.round(budget.totalBudget / storyCount) : 0;
  const overheadTotal = budget.orchestrationOverhead + budget.dodOverhead + budget.reworkBuffer;
  const overheadPct = budget.totalBudget > 0
    ? ((overheadTotal / budget.totalBudget) * 100).toFixed(1)
    : '0';

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-xs text-gray-400 uppercase tracking-wide">Total Tokens</p>
        <p className="text-lg font-bold text-ldaja-blue mt-1">{fmtTokens(budget.totalBudget)}</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-xs text-gray-400 uppercase tracking-wide">Per Story Avg</p>
        <p className="text-lg font-bold text-gray-700 mt-1">{fmtTokens(perStoryAvg)}</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-xs text-gray-400 uppercase tracking-wide">Overhead</p>
        <p className="text-lg font-bold text-amber-600 mt-1">{overheadPct}%</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-xs text-gray-400 uppercase tracking-wide">Net Available</p>
        <p className="text-lg font-bold text-green-600 mt-1">{fmtTokens(budget.netAvailable)}</p>
      </div>
    </div>
  );
}
